import React, { useMemo, useState } from "react";
import { DownloadResultsService } from "@application/useCases";
import { FileMetadata } from "@domain/schemas/BI";
import { triggerArtifactDownloads } from "@utils/triggerArtifactDownloads";
import { useNotifications } from "@presentation/components/notifications";
import { ArtifactDownloadDialog } from "./ArtifactDownloadDialog";

type DownloadTarget = {
  title: string;
  loader: () => Promise<FileMetadata[]>;
  downloader: (selectedFiles: string[]) => Promise<string>;
};

type UseArtifactDownloadDialogProperties = {
  downloadResultsService: DownloadResultsService;
};

export const useArtifactDownloadDialog = ({ downloadResultsService }: UseArtifactDownloadDialogProperties) => {
  const { pushNotification } = useNotifications();
  const [target, setTarget] = useState<DownloadTarget | null>(null);
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const openDialog = async (nextTarget: DownloadTarget) => {
    setTarget(nextTarget);
    setFiles([]);
    setErrorMessage(null);
    setIsLoading(true);

    try {
      const availableFiles = await nextTarget.loader();
      if (availableFiles.length === 0) {
        try {
          const directDownloadUrl = await nextTarget.downloader([]);
          if (directDownloadUrl?.trim()) {
            triggerArtifactDownloads(directDownloadUrl);
            closeDialog();
            return;
          }
        } catch {
          // Keep dialog open with an explanatory message when direct fallback also fails.
        }

        setFiles([]);
        setErrorMessage("No downloadable files were found.");
        return;
      }

      setFiles(availableFiles);
    } catch {
      setErrorMessage("Failed to load downloadable files.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeDialog = () => {
    setTarget(null);
    setFiles([]);
    setErrorMessage(null);
    setIsLoading(false);
  };

  const dialog = useMemo(() => {
    if (!target) {
      return null;
    }

    return (
      <ArtifactDownloadDialog
        title={target.title}
        files={files}
        isLoading={isLoading}
        errorMessage={errorMessage}
        onClose={closeDialog}
        onDownload={async (selectedFiles) => {
          try {
            const downloadUrl = await target.downloader(selectedFiles);
            const startedDownloads = triggerArtifactDownloads(downloadUrl);

            // A successful response with no URLs must not look like a completed
            // download; closing here would silently report success for zero files.
            if (startedDownloads === 0) {
              pushNotification("No files were available to download.", "error", 10_000);
              return;
            }

            closeDialog();
          } catch {
            pushNotification("Failed to start download.", "error", 10_000);
          }
        }}
      />
    );
  }, [errorMessage, files, isLoading, pushNotification, target]);

  return {
    artifactDownloadDialog: dialog,
    openJobDownloadDialog: async (jobId: string) =>
      openDialog({
        title: "Download job results",
        loader: () => downloadResultsService.listJobResultFiles(jobId),
        downloader: (selectedFiles) => downloadResultsService.getDownloadURL(jobId, selectedFiles),
      }),
    openStepDownloadDialog: async (jobId: string, jobPipelineStepId: string) =>
      openDialog({
        title: "Download step artifacts",
        loader: () => downloadResultsService.listStepArtifactFiles(jobId, jobPipelineStepId),
        downloader: (selectedFiles) => downloadResultsService.getStepDownloadURL(jobId, jobPipelineStepId, selectedFiles),
      }),
    openBatchDownloadDialog: async (jobId: string, jobPipelineStepId: string, batchId: string) =>
      openDialog({
        title: "Download batch artifacts",
        loader: () => downloadResultsService.listBatchArtifactFiles(jobId, jobPipelineStepId, batchId),
        downloader: (selectedFiles) => downloadResultsService.getBatchDownloadURL(jobId, jobPipelineStepId, batchId, selectedFiles),
      }),
  };
};
